/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    type FormEvent,
    type KeyboardEvent,
    type ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react';
import { useNavigate } from '@/hooks/use-navigate';
import { Input } from '@/components/forms/input';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { Search as SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Suggestions from '@/components/search/suggestions';
import { useSearchSuggestions } from '@/hooks/use-search-suggestions';
import { useSearchSuggestionsAdapter } from '@/providers/search-suggestions';
import { SuggestionsKeyboardContext, type KeyboardNavContextValue } from '@/components/search/keyboard-nav-context';
import { searchUrlBuilder } from '@/lib/url';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { useAnalytics } from '@/hooks/use-analytics';
import { getSessionJSONItem, setSessionJSONItem, clearSessionJSONItem } from '@/lib/utils';
import { openShopperAgentAndSendMessage } from '@/components/shopper-agent';
import { validateShopperAgentConfig } from '@/components/shopper-agent/shopper-agent.utils';
import { UITarget } from '@/targets/ui-target';

const RECENT_SEARCH_LIMIT = 5;
const RECENT_SEARCH_KEY = 'recent-search-key';
const RECENT_SEARCH_MIN_LENGTH = 3;
const LISTBOX_ID = 'search-suggestions-listbox';

// Tailwind's default `md` breakpoint — must mirror `suggestions-section.tsx`,
// which switches between vertical (mobile) and horizontal (desktop) layouts
// at `md:` so the keyboard-navigation row order tracks the visible tree.
const DESKTOP_QUERY = '(min-width: 768px)';

function subscribeToDesktopQuery(callback: () => void): () => void {
    const mql = globalThis.matchMedia?.(DESKTOP_QUERY);
    mql?.addEventListener('change', callback);
    return () => mql?.removeEventListener('change', callback);
}

function getDesktopSnapshot(): boolean {
    return globalThis.matchMedia?.(DESKTOP_QUERY)?.matches ?? false;
}

function getDesktopServerSnapshot(): boolean {
    // Mirrors the SSR default in product-tile/context — mobile-first so
    // hydration is deterministic. The popover only renders after the
    // user focuses/types, by which time the client snapshot is live.
    return false;
}

function useIsDesktop(): boolean {
    return useSyncExternalStore(subscribeToDesktopQuery, getDesktopSnapshot, getDesktopServerSnapshot);
}

type RowSection = 'Phrase' | 'Category' | 'Product' | 'Recent' | 'ViewAll';

interface NavRow {
    /** DOM id used by `aria-activedescendant` and row `id` attributes. */
    id: string;
    link: string;
    name: string;
    section: RowSection;
}

export default function SearchBar(): ReactElement {
    const { t } = useTranslation('header');
    const navigate = useNavigate();
    const config = useConfig<AppConfig>();
    const { trackCommerceAgentEngagement } = useAnalytics();
    const adapter = useSearchSuggestionsAdapter();
    const isDesktop = useIsDesktop();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [query, setQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
    const [recentSearches, setRecentSearches] = useState<string[]>(
        () => getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || []
    );
    // When the popover is dismissed because the page scrolled we must
    // hold it closed until the user explicitly re-engages (focus / type);
    // otherwise an in-flight suggestion fetch landing right after the
    // scroll would re-open the popover via the `[query, transformedSuggestions]`
    // effect below.
    const dismissedByScrollRef = useRef(false);

    // Adapter-driven autocomplete. The provider resolves which
    // implementation runs (mock / constructor / einstein) — the header
    // never branches on the data source. The hook owns its own
    // setTimeout debounce + AbortController-based cancellation; do
    // NOT add a second debounce layer here (regression: dual debounce
    // produces two same-payload fetches per settled query, one of which
    // is immediately aborted by the other).
    const { data: transformedSuggestions } = useSearchSuggestions({
        q: query,
        debounceMs: config.pages.search.suggestionsDebounce,
        enabled: query.trim().length >= RECENT_SEARCH_MIN_LENGTH,
    });

    const refreshRecentSearches = useCallback(() => {
        setRecentSearches(getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || []);
    }, []);

    const saveRecentSearch = useCallback(
        (searchText: string) => {
            let searches = getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || [];
            searches = searches.filter((savedSearchTerm) => {
                return searchText.toLowerCase() !== savedSearchTerm.toLowerCase();
            });
            searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT);
            setSessionJSONItem(RECENT_SEARCH_KEY, searches);
            refreshRecentSearches();
        },
        [refreshRecentSearches]
    );

    // Flat navigable rows in the visual reading order of the *currently
    // visible* layout. Mobile renders Categories → Products → Popular
    // top-to-bottom; desktop renders Categories → Popular on the left
    // column with Products in the right column followed by the View
    // all link. Keyboard arrows must follow the order users see.
    //
    // ID layout: both the mobile (`block md:hidden`) and desktop
    // (`hidden md:flex`) trees in `suggestions-section.tsx` stay
    // mounted at all times — only CSS hides the inactive one. To
    // avoid duplicate DOM ids, each tree namespaces its row ids with
    // a layout prefix (`m-` / `d-`); the rows array uses the prefix
    // for whichever tree is currently visible so `activeId` resolves
    // to the on-screen element (relevant for `scrollIntoView`, which
    // is a no-op on `display: none` elements).
    const rows = useMemo<NavRow[]>(() => {
        const list: NavRow[] = [];
        const layoutPrefix = isDesktop ? 'd' : 'm';
        const firstPhrase = transformedSuggestions?.phraseSuggestions?.[0];
        const showDidYouMean = Boolean(firstPhrase && firstPhrase.exactMatch === false);
        const categories = transformedSuggestions?.categorySuggestions ?? [];
        const products = transformedSuggestions?.productSuggestions ?? [];
        const popular = transformedSuggestions?.popularSearchSuggestions ?? [];

        const hasSuggestions = categories.length > 0 || products.length > 0 || popular.length > 0;

        if (hasSuggestions) {
            if (showDidYouMean && firstPhrase) {
                list.push({
                    id: `${layoutPrefix}-sf-row-phrase-0`,
                    link: firstPhrase.link,
                    name: firstPhrase.name,
                    section: 'Phrase',
                });
            }
            categories.forEach((s, i) => {
                list.push({
                    id: `${layoutPrefix}-sf-row-cat-${i}`,
                    link: s.link,
                    name: s.name,
                    section: 'Category',
                });
            });
            if (isDesktop) {
                popular.forEach((s, i) => {
                    list.push({
                        id: `${layoutPrefix}-sf-row-popular-${i}`,
                        link: s.link,
                        name: s.name,
                        section: 'Phrase',
                    });
                });
                products.forEach((s, i) => {
                    list.push({
                        id: `${layoutPrefix}-sf-row-prod-${i}`,
                        link: s.link,
                        name: s.name,
                        section: 'Product',
                    });
                });
                if (products.length > 0) {
                    list.push({
                        id: `${layoutPrefix}-sf-row-viewall`,
                        link: searchUrlBuilder(transformedSuggestions?.searchPhrase || ''),
                        name: t('searchSuggestions'),
                        section: 'ViewAll',
                    });
                }
            } else {
                products.forEach((s, i) => {
                    list.push({
                        id: `${layoutPrefix}-sf-row-prod-${i}`,
                        link: s.link,
                        name: s.name,
                        section: 'Product',
                    });
                });
                popular.forEach((s, i) => {
                    list.push({
                        id: `${layoutPrefix}-sf-row-popular-${i}`,
                        link: s.link,
                        name: s.name,
                        section: 'Phrase',
                    });
                });
            }
        } else {
            // Recent searches render in a single tree (not duplicated
            // across mobile/desktop layouts), so they don't need a
            // layout prefix to disambiguate DOM ids.
            recentSearches.forEach((s, i) => {
                list.push({
                    id: `sf-row-recent-${i}`,
                    link: searchUrlBuilder(s),
                    name: s,
                    section: 'Recent',
                });
            });
        }
        return list;
    }, [transformedSuggestions, recentSearches, isDesktop, t]);

    // Highlight always resets when query/suggestions change so the
    // pointer can't outlive the row it was pointing at.
    useEffect(() => {
        setHighlightedIndex(null);
    }, [query, transformedSuggestions, isDesktop]);

    // Guard against stale indices (e.g. when recentSearches shrinks).
    useEffect(() => {
        if (highlightedIndex != null && highlightedIndex >= rows.length) {
            setHighlightedIndex(null);
        }
    }, [highlightedIndex, rows.length]);

    const activeId = highlightedIndex != null ? rows[highlightedIndex]?.id : undefined;

    // Keep the highlighted row visible inside the scrollable panel.
    // `block: 'nearest'` minimizes movement and the window-level
    // scroll-to-dismiss listener does not fire because DOM `scroll`
    // events from a scrollable child do not bubble to `window`.
    // The optional-chain on the method itself guards jsdom (and other
    // non-browser environments) where `scrollIntoView` is undefined.
    useEffect(() => {
        if (!activeId) return;
        const el = document.getElementById(activeId);
        el?.scrollIntoView?.({ block: 'nearest' });
    }, [activeId]);

    const setHighlightedById = useCallback(
        (id: string) => {
            const idx = rows.findIndex((r) => r.id === id);
            if (idx >= 0) setHighlightedIndex(idx);
        },
        [rows]
    );

    const keyboardContext = useMemo<KeyboardNavContextValue>(
        () => ({ activeId, setHighlightedById }),
        [activeId, setHighlightedById]
    );

    const shouldOpenPopover = useCallback(() => {
        if (dismissedByScrollRef.current) return;
        const searchSuggestionsAvailable =
            transformedSuggestions &&
            (transformedSuggestions.categorySuggestions.length > 0 ||
                transformedSuggestions.productSuggestions.length > 0 ||
                (transformedSuggestions.popularSearchSuggestions?.length ?? 0) > 0);

        if (
            (document.activeElement === inputRef.current && recentSearches.length > 0) ||
            (searchSuggestionsAvailable && inputRef.current?.value && inputRef.current.value.length > 0)
        ) {
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    }, [transformedSuggestions, recentSearches.length]);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            dismissedByScrollRef.current = false;
            const value = e.target.value;
            setQuery(value);
            shouldOpenPopover();
        },
        [shouldOpenPopover]
    );

    // Track focus once per focus event so the Constructor session is
    // attributed before any typing happens (Sprint 1 ACs).
    const handleInputFocus = useCallback(() => {
        dismissedByScrollRef.current = false;
        refreshRecentSearches();
        try {
            adapter?.trackInputFocus();
        } catch {
            // Beacons must never break the popover.
        }
        shouldOpenPopover();
    }, [adapter, shouldOpenPopover, refreshRecentSearches]);

    // Reopen path that does NOT require focus to change. Page-scroll
    // dismissal leaves focus on the input, so `onFocus` never refires
    // when the user clicks the input again. This handler clears the
    // dismissed-by-scroll gate and asks the popover to reconsider. It
    // deliberately omits `adapter.trackInputFocus()` because the focus
    // beacon already fired when the user first focused the input — a
    // click on an already-focused input is not a new focus event.
    const handleInputClick = useCallback(() => {
        dismissedByScrollRef.current = false;
        shouldOpenPopover();
    }, [shouldOpenPopover]);

    const handleSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            if (inputRef.current?.value?.trim()) {
                const searchQuery = inputRef.current.value.trim();
                saveRecentSearch(searchQuery);
                setShowSuggestions(false);
                try {
                    adapter?.trackSearchSubmit(searchQuery);
                } catch {
                    // Beacons must never block navigation.
                }
                void navigate(`/search?q=${encodeURIComponent(searchQuery)}`, {
                    state: { query: searchQuery },
                });
            }
        },
        [adapter, navigate, saveRecentSearch]
    );

    const closeAndNavigate = useCallback(
        (link: string) => {
            inputRef.current?.blur();
            setShowSuggestions(false);
            setHighlightedIndex(null);
            const term = query.trim();
            // Best-effort attribution — if we can't classify the link
            // (rare), forward what we have and let the adapter decide
            // whether to drop the event.
            const lcLink = link.toLowerCase();
            const section = lcLink.startsWith('/product/')
                ? 'Products'
                : lcLink.startsWith('/category/')
                  ? 'Category'
                  : 'Search Suggestions';
            const itemId = lcLink.startsWith('/product/')
                ? link.slice('/product/'.length).split(/[/?#]/)[0]
                : lcLink.startsWith('/category/')
                  ? link.slice('/category/'.length).split(/[/?#]/)[0]
                  : undefined;
            const value = inputRef.current?.value?.trim() || term;
            try {
                adapter?.trackAutocompleteSelect({ term, value, section, ...(itemId && { itemId }) });
            } catch {
                // Beacons must never block navigation.
            }
            setQuery('');
            if (inputRef.current) {
                inputRef.current.value = '';
            }
            if (link) {
                void navigate(link);
            }
        },
        [adapter, navigate, query]
    );

    const clearRecentSearches = useCallback(() => {
        clearSessionJSONItem(RECENT_SEARCH_KEY);
        setRecentSearches([]);
        setShowSuggestions(false);
        setHighlightedIndex(null);
    }, []);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            // INT-43 Sprint 1: Esc closes the popover when focus stays
            // in the input. Main no longer renders Radix `PopoverContent`,
            // so the input-level handler is the only Esc dismissal path.
            if (e.key === 'Escape') {
                if (showSuggestions) {
                    e.preventDefault();
                    setShowSuggestions(false);
                    setHighlightedIndex(null);
                }
                return;
            }

            // Open-or-navigate keys only matter when the popover is
            // open AND has navigable rows. Closed popover or empty
            // rows = fall through to the input's native behavior.
            if (!showSuggestions || rows.length === 0) return;

            switch (e.key) {
                case 'ArrowDown': {
                    e.preventDefault();
                    setHighlightedIndex((curr) => (curr == null ? 0 : (curr + 1) % rows.length));
                    return;
                }
                case 'ArrowUp': {
                    e.preventDefault();
                    setHighlightedIndex((curr) =>
                        curr == null ? rows.length - 1 : (curr - 1 + rows.length) % rows.length
                    );
                    return;
                }
                case 'Home': {
                    e.preventDefault();
                    setHighlightedIndex(0);
                    return;
                }
                case 'End': {
                    e.preventDefault();
                    setHighlightedIndex(rows.length - 1);
                    return;
                }
                case 'Enter': {
                    if (highlightedIndex == null) return;
                    const row = rows[highlightedIndex];
                    if (!row) return;
                    e.preventDefault();
                    // Route every Enter through closeAndNavigate so the
                    // tracking + recent-search-save semantics match a
                    // mouse selection exactly.
                    closeAndNavigate(row.link);
                    break;
                }
                default:
                    // Other keys (including Tab / Shift+Tab) fall through to
                    // the input's native behavior; Tab naturally moves focus,
                    // which the existing onBlur listener uses to close the
                    // popover.
                    break;
            }
        },
        [showSuggestions, rows, highlightedIndex, closeAndNavigate]
    );

    const showShopperAgent =
        (config.commerceAgent?.enabled === 'true' || config.commerceAgent?.enabled === true) &&
        validateShopperAgentConfig(config.commerceAgent);

    const onShopperAgentClick = useCallback(() => {
        const searchText = inputRef.current?.value?.trim() ?? query.trim();
        void trackCommerceAgentEngagement({ surface: 'search' });
        setShowSuggestions(false);
        setHighlightedIndex(null);
        setQuery('');
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        openShopperAgentAndSendMessage(searchText);
    }, [query, trackCommerceAgentEngagement]);

    useEffect(() => {
        shouldOpenPopover();
    }, [query, transformedSuggestions, shouldOpenPopover]);

    // Fire `trackAutocompleteView` once per (query, sections-snapshot)
    // tuple so the same impression isn't reported on every render. The
    // ref-based dedupe matches the pattern already used in
    // `suggestions-section.tsx` for the SCAPI `viewSearchSuggestions`
    // event so the two systems stay in sync.
    const lastViewKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (!adapter || !transformedSuggestions) return;
        const trimmed = query.trim();
        if (trimmed.length < RECENT_SEARCH_MIN_LENGTH) return;
        const phraseCount = transformedSuggestions.phraseSuggestions?.length ?? 0;
        const productCount = transformedSuggestions.productSuggestions?.length ?? 0;
        const categoryCount = transformedSuggestions.categorySuggestions?.length ?? 0;
        const popularCount = transformedSuggestions.popularSearchSuggestions?.length ?? 0;
        const sectionsCount =
            (phraseCount > 0 ? 1 : 0) +
            (productCount > 0 ? 1 : 0) +
            (categoryCount > 0 ? 1 : 0) +
            (popularCount > 0 ? 1 : 0);
        if (sectionsCount === 0) return;
        const key = `${trimmed}|${phraseCount}|${productCount}|${categoryCount}|${popularCount}`;
        if (lastViewKeyRef.current === key) return;
        lastViewKeyRef.current = key;
        try {
            adapter.trackAutocompleteView(trimmed, sectionsCount);
        } catch {
            // Beacons must never break the popover.
        }
    }, [adapter, query, transformedSuggestions]);

    // Close the flyout when the page scrolls. The header is not sticky
    // today, so a page scroll leaves the flyout visually detached from
    // its trigger; dismissing it is the expected UX. Two correctness
    // details:
    //   1. We listen on `window` rather than the panel — DOM `scroll`
    //      events do not bubble from inner scrollable elements to the
    //      window, so scrolling inside the suggestions panel itself
    //      does not dismiss it.
    //   2. A short grace period prevents mobile virtual-keyboard
    //      viewport-resize scrolls (which fire `window` scroll on first
    //      focus) from closing the popover before the user can interact.
    useEffect(() => {
        if (!showSuggestions) return;
        let attached = false;
        const handleScroll = () => {
            dismissedByScrollRef.current = true;
            setShowSuggestions(false);
            setHighlightedIndex(null);
        };
        const timer = window.setTimeout(() => {
            window.addEventListener('scroll', handleScroll, { passive: true });
            attached = true;
        }, 150);
        return () => {
            window.clearTimeout(timer);
            if (attached) window.removeEventListener('scroll', handleScroll);
        };
    }, [showSuggestions]);

    return (
        <UITarget targetId="sfcc.header.search.input">
            <Popover open={showSuggestions}>
                <form onSubmit={handleSubmit} className="relative z-10">
                    <div className="relative">
                        <PopoverTrigger asChild>
                            <Input
                                ref={inputRef}
                                type="text"
                                placeholder={t('searchPlaceholder')}
                                className="w-full pl-10 focus-visible:border-header-foreground focus-visible:ring-1 focus-visible:ring-header-foreground"
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                onClick={handleInputClick}
                                onBlur={() => setShowSuggestions(false)}
                                onKeyDown={handleKeyDown}
                                aria-label={t('searchPlaceholder')}
                                aria-autocomplete="list"
                                aria-expanded={showSuggestions}
                                aria-haspopup="listbox"
                                aria-controls={LISTBOX_ID}
                                aria-activedescendant={activeId}
                                role="combobox"
                                data-testid="header-search"
                            />
                        </PopoverTrigger>
                        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2" />
                    </div>
                </form>
                {/* Note: Using a fixed div instead of PopoverContent because the search
                    suggestions panel is designed to span the full page width while the trigger is input with limited width in the header.
                    Using w-screen on Radix PopoverContent will include the scrollbar width, while the rest of page does not aware of scrollbar width
                    this causing the content in Popover to completely miss aligned with the rest of the layout despite using the same section gutter class.
                    Therefore, we go with traditional div to get control over styling for search result area*/}
                {showSuggestions && (
                    <div
                        id={LISTBOX_ID}
                        className="fixed left-0 right-0 z-50 border-b shadow-[0px_1px_12px_rgba(0,0,0,0.25)] max-h-[min(70vh,32rem)] overflow-y-auto bg-popover text-popover-foreground"
                        style={{ top: 'var(--header-height)' }}
                        role="listbox"
                        aria-label={t('searchSuggestions')}>
                        <SuggestionsKeyboardContext.Provider value={keyboardContext}>
                            <Suggestions
                                searchSuggestions={transformedSuggestions}
                                recentSearches={recentSearches}
                                closeAndNavigate={closeAndNavigate}
                                clearRecentSearches={clearRecentSearches}
                                showShopperAgent={showShopperAgent}
                                onShopperAgentClick={onShopperAgentClick}
                            />
                        </SuggestionsKeyboardContext.Provider>
                    </div>
                )}
            </Popover>
        </UITarget>
    );
}
